import React, { Dispatch, SetStateAction, useEffect, useRef } from 'react';
import DOMPurify from 'dompurify';
import { SectionProps } from '../../activityComponents/models';
import { sub } from 'date-fns';
import { useSelector } from 'react-redux';
import { selectWebskin } from 'reducers/auth';
import { selectPrivacyPolicy } from 'reducers/landingPage';
import { paths } from 'appConstants';
import { PreventPageClose } from 'components/confirmPageClose/ConfirmPageClose';
import { useTranslation } from 'react-i18next';
import i18n from 'i18n/i18n';

interface AssessmentSubmissionProps {
  handleToggle: () => void;
  isConfirmed: boolean;
  assessmentName?: string | null;
  applicantName?: string;
  style?: React.CSSProperties;
  extraClassName?: string;
  submissionPageText: string | null;
}

const getCheckboxText = (
  html: string
): { checkboxText: string | null; cleanedHtml: string | null } => {
  // Find position of marker
  const markerIndex = html.indexOf('[[AddCheckbox]]');
  if (markerIndex === -1) return { checkboxText: null, cleanedHtml: html };

  // Find enclosing <p> tag before marker
  const pStartIndex = html.lastIndexOf('<p', markerIndex);
  if (pStartIndex === -1) return { checkboxText: null, cleanedHtml: null };

  // Find closing </p> tag after marker
  const contentStart = html.indexOf('>', pStartIndex) + 1;
  const contentEnd = html.indexOf('</p>', markerIndex);

  // Extract text and remove marker
  const checkboxText = html
    .substring(contentStart, contentEnd)
    .replace('[[AddCheckbox]]', '')
    .trim();

  // Remove entire <p> tag and contents
  const cleanedHtml = html.slice(0, pStartIndex) + html.slice(contentEnd + 4);
  console.log(checkboxText, cleanedHtml);
  return { checkboxText, cleanedHtml };
};

const AssessmentSubmission: React.FC<AssessmentSubmissionProps> = ({
  handleToggle,
  isConfirmed,
  assessmentName,
  submissionPageText,
  style,
  extraClassName,
}) => {
  const privacyPolicy = useSelector(selectPrivacyPolicy);
  const webSkinDetails = useSelector(selectWebskin);
  const confirmationBoxRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation('Assessment+Submission', { i18n });

  useEffect(() => {
    let checkbox: HTMLInputElement | null | undefined = null;
    if (confirmationBoxRef.current) {
      const checkboxElement = confirmationBoxRef.current?.querySelector(
        'input[type="checkbox"]'
      );
      if (checkboxElement) {
        checkbox = checkboxElement as HTMLInputElement;
        checkbox.checked = isConfirmed;
        checkbox.addEventListener('change', (e) => {
          console.log('Checkbox clicked', e);
          handleToggle();
        });
      }
      // In preparation for accessibility improvements deployment
      // const elements = confirmationBoxRef.current?.querySelectorAll(
      //   'p, h1, ul, li, a, b'
      // );
      // if (style && elements) {
      //   elements.forEach((element) => {
      //     if (element instanceof HTMLElement) {
      //       for (const [key, value] of Object.entries(style)) {
      //         element.style[key as any] = value;
      //       }
      //     }
      //   });

      return () => {
        if (checkbox) {
          checkbox.removeEventListener('change', handleToggle);
        }
      };
    }
  }, [handleToggle]);

  if (submissionPageText) {
    const { checkboxText, cleanedHtml } = getCheckboxText(submissionPageText);
    return checkboxText && cleanedHtml ? (
      <>
        <PreventPageClose />
        <div className="AssessmentSubmission" style={style}>
          <div style={style} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(cleanedHtml) }} />
          {/* // Current master version */}
          {/* <div className="AssessmentConfirmationBox">
            <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(checkboxText) }} />
            <div className="CheckboxFieldContainer">
              <label className="Label">
                <input
                  checked={isConfirmed}
                  type="checkbox"
                  className="CheckboxInput"
                  name={'checkbox'}
                  onChange={handleToggle}
                />
                <span className="Checkmark"></span>
              </label>
            </div>
          </div> */}
          <div className="CheckboxBlock">
            <input
              name="checkbox"
              id="checkbox"
              type="checkbox"
              checked={isConfirmed}
              onChange={handleToggle}
            />
            <label
              htmlFor="checkbox"
              className="CustomInput"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(checkboxText) }}
            />
          </div>
        </div>
      </>
    ) : cleanedHtml ? (
      <>
        <PreventPageClose />
        <div
          className="AssessmentSubmission"
          ref={confirmationBoxRef}
          style={style}
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(cleanedHtml) }}
        />
      </>
      
    ) : (
      SubmissionFallback(style, assessmentName, isConfirmed, handleToggle, t)
    );
  }

  return SubmissionFallback(style, assessmentName, isConfirmed, handleToggle, t);
};

export { AssessmentSubmission };

const SubmissionFallback = (
  style: React.CSSProperties | undefined,
  assessmentName: string | null | undefined,
  isConfirmed: boolean,
  handleToggle: () => void,
  t: (key: string, options?: any) => string
) => {
  const privacyPolicy = useSelector(selectPrivacyPolicy);

  return (
    <>
      <PreventPageClose />
      <div className="AssessmentSubmission" style={style}>
        <h1>{t('SSCandidate_Assessment_Submission_Nearly_Done')}</h1>
        <p>{t('SSCandidate_Assessment_Submission_Thanks_Time')}</p>
        <p>{t('SSCandidate_Assessment_Submission_Before_You_Finish')}</p>
        <ul style={style}>
          <li style={style}>
            {t('SSCandidate_Assessment_Submission_Happy_Store_Responses', {
              val1: assessmentName ? `${assessmentName} ` : '',
            })}
          </li>
          <li style={style}>
            {t('SSCandidate_Assessment_Submission_Honest_Information')}
          </li>
        </ul>
        <p className="" style={style}>
          <b style={style}>
            {t('SSCandidate_Assessment_Submission_Data_Info_Prefix')}
            <a
              href={`${privacyPolicy ? paths.privacyPolicy.url : "https://www.clevry.com/en/privacy-policy/"}`}
              target="_blank"
              rel="noopener noreferrer"
              style={style}
            >
              {t('SSCandidate_Assessment_Submission_Privacy_Policy')}
            </a>
            {t('SSCandidate_Assessment_Submission_Data_Info_Suffix')}
          </b>
        </p>
        <div className="CheckboxBlock">
          <input name="checkbox" id="checkbox" type="checkbox" checked={isConfirmed} onChange={handleToggle} />
          <label htmlFor="checkbox" className="CustomInput"><b>{t('SSCandidate_Assessment_Submission_Confirm_Understand')}</b></label>
        </div>
      </div>
    </>
  );
};
